import { useCallback, useState } from "react"

import { Button, Col, Container, Modal, Row } from "react-bootstrap"
import { useSelector } from "react-redux"

import EmailFormList, { EmailItem } from "./EmailFormList"
import MobileFormList, { MobileItem } from "./MobileFormList"
import useAuthFormProcessPrint from "../../../api/hooks/useAuthFormProcessPrint"
import OrderResultRecord from "../../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { BuildUserAccess } from "../../../authentication/BuildUserAccess"
import { ScheduleStatus } from "../../../config/Constants"
import printPdf from "../../../utils/printPdf"
import ActionButton from "../../ActionButton"

interface AuthFormModalProps {
  details: OrderResultRecord
  isAfterUpdate?: boolean
  showEmail?: boolean
  showMobile?: boolean
  showEditOrder?: boolean
  onHide?: (refreshGrid?: boolean) => void
  onEditOrder?: (data: OrderResultRecord) => void
}

function AuthFormModal({
  details,
  isAfterUpdate = false,
  showEmail = true,
  showMobile = true,
  showEditOrder = true,
  onHide = () => {},
  onEditOrder = () => {},
}: AuthFormModalProps) {
  const { canEditPendingOrders } = useSelector(BuildUserAccess)
  const [emailItems, setEmailItems] = useState<EmailItem[]>([{ id: 0, value: "" }])
  const [mobileItems, setMobileItems] = useState<MobileItem[]>([{ id: 0, value: "" }])
  const { processing, printAuthForm } = useAuthFormProcessPrint()

  const handlePrintClick = useCallback(() => {
    printAuthForm({
      formFoxTestAN: Number(details.FormFoxTestAN || 0),
      personId: details.PersonID || 0,
      vendorId: details.CollectionVendorID || 0,
      callback: (response) => response && printPdf(response),
    })
  }, [details, printAuthForm])

  const handleEditOrderClick = useCallback(() => {
    if (details && showEditOrder) {
      onEditOrder(details)
    }
  }, [details, onEditOrder])

  const isEmailListHidden =
    !showEmail || details.DateCollected || details.ScheduleStatusID === ScheduleStatus.Cancelled || details.IsExpired

  const isMobileListHidden =
    !showMobile || details.DateCollected || details.ScheduleStatusID === ScheduleStatus.Cancelled || details.IsExpired

  const isEditOrderHidden =
    !canEditPendingOrders ||
    !showEditOrder ||
    isAfterUpdate ||
    details.DateCollected ||
    details.ScheduleStatusID === ScheduleStatus.Cancelled ||
    details.IsExpired ||
    details.ScheduleStatusID === 33

  return (
    <Modal show onHide={() => onHide(false)} size="lg" dialogClassName="dark">
      <Modal.Header closeButton>
        <Modal.Title>
          {!isAfterUpdate && (
            <h4 className="text-uppercase">
              <span>AUTHORIZATION #{details.FormFoxTestAN}</span>
            </h4>
          )}
          {isAfterUpdate && (
            <h4 className="text-uppercase">
              <span>Resend Authorization #{details.FormFoxTestAN}</span>
            </h4>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <Container fluid>
          <Row className="pb-2">
            <Col xs={3} lg={2}>
              Print form:
            </Col>
            <Col xs={9} lg={10} className="d-flex">
              <ActionButton size="sm" className="fixed-w10" onClick={handlePrintClick} spin={processing}>
                Print PDF
              </ActionButton>
            </Col>
          </Row>
          {!isEmailListHidden && (
            <EmailFormList emailItems={emailItems} setEmailItems={setEmailItems} details={details} />
          )}
          {!isMobileListHidden && (
            <MobileFormList mobileItems={mobileItems} setMobileItems={setMobileItems} details={details} />
          )}
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {!isEditOrderHidden && (
          <Button size="sm" onClick={handleEditOrderClick} className="mr-auto fixed-w5">
            Edit Order
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={() => onHide()}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AuthFormModal
